import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { devFilesApi, appCfgApi, adminDynamicApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useLookups } from '../../context/LookupContext';
import {
  RefreshCw, Database, Key, Link, Zap, Save, Table2,
  ChevronLeft, ChevronRight, Search, ArrowUp, ArrowDown, Tags, Settings, X
} from 'lucide-react';
import StuiSelect from '../../components/common/StuiSelect';
import SourceDatabasePicker, { useSourceDatabase } from '../../components/common/SourceDatabasePicker';

/**
 * FieldManagerPage - Schema Browser + Field Metadata Editor
 * Replaces SchemaViewerPage with full CRUD for field configuration
 */
const FieldManagerPage = () => {
  const { notify } = useNotifications();
  const { lookups } = useLookups();
  const lookupCategories = Object.keys(lookups).sort();

  // View state
  const [listCollapsed, setListCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Data state
  const [schema, setSchema] = useState(null);
  const [fieldMetadata, setFieldMetadata] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Edit state
  const [editedFields, setEditedFields] = useState({});

  // Sort state
  const [sortCol, setSortCol] = useState('field_Name');
  const [sortDir, setSortDir] = useState('asc');

  // Label Config modal state
  const [labelConfigField, setLabelConfigField] = useState(null);

  // FK Config modal state
  const [fkConfigField, setFkConfigField] = useState(null);
  const [adminTables, setAdminTables] = useState([]);
  const [adminColumnCache, setAdminColumnCache] = useState({});

  // The schema shown comes from the source picker (the template unless another database is
  // picked). Sync ignores it: it always reads the template.
  const { databases, source, setSource, ready: sourceReady } = useSourceDatabase();

  // Load all data
  const loadData = useCallback(async () => {
    if (!sourceReady) return;
    try {
      setLoading(true);
      const [schemaRes, metadataRes] = await Promise.all([
        devFilesApi.getLiveSchema(source || undefined),
        appCfgApi.getAllFieldMetadata()
      ]);
      setSchema(schemaRes.data);
      setFieldMetadata(metadataRes.data);

      // Auto-select first table if none selected
      if (!selectedTable && schemaRes.data?.tables?.length > 0) {
        setSelectedTable(schemaRes.data.tables[0].name);
      }
    } catch (err) {
      notify.error('Failed to load data: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceReady, source]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get tables list (filter out system/config tables)
  const tables = useMemo(() => {
    if (!schema?.tables) return [];
    return schema.tables
      .map(t => t.name)
      .filter(name =>
        !name.startsWith('AppCfg_') &&
        !name.startsWith('vLU_') &&
        name !== 'sysdiagrams'
      )
      .sort();
  }, [schema]);

  // Get columns for a table (used for FK display field dropdown)
  const getTableColumns = (tableName) => {
    if (!tableName || !schema?.tables) return [];
    const table = schema.tables.find(t => t.name === tableName);
    return table?.columns?.map(c => c.name).filter(n => n !== 'ID') || [];
  };

  // Filter tables by search
  const filteredTables = useMemo(() => {
    if (!searchTerm) return tables;
    return tables.filter(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [tables, searchTerm]);

  // Get metadata for selected table
  const tableFields = useMemo(() => {
    if (!selectedTable) return [];
    return fieldMetadata
      .filter(f => f.table_Name === selectedTable)
      .sort((a, b) => {
        let aVal = a[sortCol];
        let bVal = b[sortCol];
        // Handle nulls — push to bottom
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        // Numeric sort for number columns
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        }
        // String sort
        const cmp = String(aVal).localeCompare(String(bVal));
        return sortDir === 'asc' ? cmp : -cmp;
      });
  }, [selectedTable, fieldMetadata, sortCol, sortDir]);

  // Get schema info for selected table
  const tableSchema = useMemo(() => {
    if (!selectedTable || !schema?.tables) return null;
    return schema.tables.find(t => t.name === selectedTable);
  }, [selectedTable, schema]);

  // Merge schema with metadata for display
  const mergedFields = useMemo(() => {
    if (!tableSchema || !tableFields.length) return tableFields;

    return tableFields.map(meta => {
      const schemaCol = tableSchema.columns.find(c => c.name === meta.field_Name);
      return {
        ...meta,
        dataType: schemaCol?.dataType || 'Unknown',
        isPrimaryKey: schemaCol?.isPrimaryKey || false,
        isIdentity: schemaCol?.isIdentity || false,
        foreignKeyTable: schemaCol?.foreignKeyTable || null,
        isNullable: schemaCol?.isNullable || false
      };
    });
  }, [tableSchema, tableFields]);

  // Track if we have unsaved changes
  const hasFieldChanges = Object.keys(editedFields).length > 0;

  // Handle field edit — clears dirty state if value reverts to original
  const handleFieldChange = (fieldId, prop, value) => {
    setEditedFields(prev => {
      const original = fieldMetadata.find(f => f.id === fieldId);
      const originalVal = original?.[prop];
      const normalize = v => (v == null || v === '') ? null : v;
      const currentEdits = { ...(prev[fieldId] || {}) };

      if (normalize(value) === normalize(originalVal)) {
        delete currentEdits[prop];
      } else {
        currentEdits[prop] = value;
      }

      if (Object.keys(currentEdits).length === 0) {
        const { [fieldId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [fieldId]: currentEdits };
    });
  };

  // Get current field value (edited or original)
  const getFieldValue = (field, prop) => {
    if (editedFields[field.id]?.[prop] !== undefined) {
      return editedFields[field.id][prop];
    }
    const propMap = {
      display_Label: field.display_Label,
      default_Width: field.default_Width,
      uI_Type: field.uI_Type,
      fK_Table: field.fK_Table,
      fK_Display_Field: field.fK_Display_Field,
      fK_Clickable: field.fK_Clickable,
      fK_Admin_DB: field.fK_Admin_DB,
      format_String: field.format_String,
      header_Vertical: field.header_Vertical,
      display_Order: field.display_Order,
      lookup_Category: field.lookup_Category,
      label_Source_Table: field.label_Source_Table,
      label_Source_Column: field.label_Source_Column,
      label_Source_FK: field.label_Source_FK,
    };
    return propMap[prop] ?? '';
  };

  // Save field changes
  const handleSaveFields = async () => {
    try {
      const updates = Object.entries(editedFields).map(([id, changes]) => {
        const original = fieldMetadata.find(f => f.id === parseInt(id));
        return {
          id: parseInt(id),
          table_Name: original.table_Name,
          field_Name: original.field_Name,
          display_Label: changes.display_Label ?? original.display_Label,
          default_Width: changes.default_Width ?? original.default_Width,
          uI_Type: changes.uI_Type ?? original.uI_Type,
          fK_Table: changes.fK_Table !== undefined ? changes.fK_Table : original.fK_Table,
          fK_Display_Field: changes.fK_Display_Field ?? original.fK_Display_Field,
          fK_Clickable: changes.fK_Clickable !== undefined ? changes.fK_Clickable : original.fK_Clickable,
          fK_Admin_DB: changes.fK_Admin_DB !== undefined ? changes.fK_Admin_DB : original.fK_Admin_DB,
          format_String: changes.format_String ?? original.format_String,
          header_Vertical: changes.header_Vertical ?? original.header_Vertical,
          display_Order: changes.display_Order !== undefined ? changes.display_Order : original.display_Order,
          lookup_Category: changes.lookup_Category !== undefined ? changes.lookup_Category : original.lookup_Category,
          label_Source_Table: changes.label_Source_Table !== undefined ? changes.label_Source_Table : original.label_Source_Table,
          label_Source_Column: changes.label_Source_Column !== undefined ? changes.label_Source_Column : original.label_Source_Column,
          label_Source_FK: changes.label_Source_FK !== undefined ? changes.label_Source_FK : original.label_Source_FK
        };
      });

      await appCfgApi.updateFieldMetadataBatch(updates);
      notify.success(`Saved ${updates.length} field(s)`);

      // Update local state instead of reloading (preserves selection)
      setFieldMetadata(prev => prev.map(field => {
        const update = updates.find(u => u.id === field.id);
        return update ? { ...field, ...update } : field;
      }));
      setEditedFields({});
    } catch (err) {
      notify.error('Failed to save: ' + (err.response?.data?.message || err.message));
    }
  };

  // Sync field metadata
  const handleSync = async () => {
    try {
      setSyncing(true);
      const result = await appCfgApi.syncFieldMetadata();
      notify.success(result.data.message);
      loadData();
    } catch (err) {
      notify.error('Sync failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setSyncing(false);
    }
  };

  // UI Type options
  const uiTypeOptions = ['text', 'number', 'currency', 'date', 'datetime', 'checkbox', 'dropdown', 'searchselect', 'textarea', 'readonly'];

  // Stats
  const tableCount = tables.length;
  const fieldCount = fieldMetadata.length;

  // Sort handler
  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const SortHeader = ({ col, label, className }) => (
    <th className={`fm-sortable ${className || ''} ${sortCol === col ? 'sorted' : ''}`} onClick={() => handleSort(col)}>
      {label}
      {sortCol === col && (sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
    </th>
  );

  if (loading) {
    return <div className="dev-page loading">Loading...</div>;
  }

  return (
    <div className="dev-module field-manager-page">
      {/* Header */}
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Database size={20} className="stui-imgupload-title-icon" />
            Field Manager
            <span className="live-badge">
              <Zap size={12} /> LIVE
            </span>
          </h1>
          <span className="dev-module-meta">{schema?.databaseName}</span>
        </div>
        <div className="dev-module-header-right">
          <SourceDatabasePicker databases={databases} value={source} onChange={setSource} />
          {hasFieldChanges && (
            <button className="crud-btn primary" onClick={handleSaveFields} title="Save Changes">
              <Save size={18} />
            </button>
          )}
          <button
            className="crud-btn"
            onClick={handleSync}
            disabled={syncing}
            title="Sync from the template database (whatever the picker shows): adds new fields, removes fields the template no longer has"
          >
            <RefreshCw size={18} className={syncing ? 'stui-spinning' : ''} />
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="fm-stats-bar">
        <span className="fm-stat"><Table2 size={14} /> {tableCount} tables</span>
        <span className="fm-stat">{fieldCount} fields</span>
      </div>

      <div className={`fm-content ${listCollapsed ? 'list-collapsed' : ''}`}>
        {/* Table List */}
        <div className="fm-list-panel">
          <div className="fm-list-header">
            <div className="fm-list-search">
              <Search size={14} />
              <input
                type="text"
                placeholder="Search tables..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              className="fm-collapse-btn"
              onClick={() => setListCollapsed(!listCollapsed)}
            >
              {listCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>
          <div className="fm-list-items">
            {filteredTables.map(table => (
              <div
                key={table}
                className={`fm-list-item ${selectedTable === table ? 'selected' : ''}`}
                onClick={() => setSelectedTable(table)}
              >
                <Table2 size={14} />
                <span>{table}</span>
                <span className="fm-list-item-count">
                  {fieldMetadata.filter(f => f.table_Name === table).length}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Field Detail */}
        <div className="fm-detail-panel">
          {selectedTable ? (
            <>
              <div className="fm-detail-header">
                <h2>{selectedTable}</h2>
                <span className="fm-detail-count">{mergedFields.length} fields</span>
              </div>
              <div className="fm-fields-grid">
                <table className="fm-fields-table">
                  <thead>
                    <tr>
                      <SortHeader col="field_Name" label="Field" />
                      <SortHeader col="dataType" label="Type" />
                      <SortHeader col="display_Label" label="Display Label" />
                      <SortHeader col="default_Width" label="Width" />
                      <SortHeader col="display_Order" label="Order" />
                      <SortHeader col="uI_Type" label="UI Type" />
                      <th className="wrap-header" title="FK Configuration">FK Config</th>
                      <SortHeader col="lookup_Category" label="Lookup" />
                      <th className="wrap-header" title="Label Source Config">Label</th>
                      <SortHeader col="header_Vertical" label="Header Vertical" className="wrap-header" />
                    </tr>
                  </thead>
                  <tbody>
                    {mergedFields.map(field => (
                      <tr key={field.id} className={editedFields[field.id] ? 'edited' : ''}>
                        <td className="fm-field-name">
                          {field.isPrimaryKey && <Key size={12} className="pk-icon" title="Primary Key" />}
                          {field.foreignKeyTable && <Link size={12} className="fk-icon" title={`FK → ${field.foreignKeyTable}`} />}
                          {field.field_Name}
                        </td>
                        <td className="fm-field-type">{field.dataType}</td>
                        <td>
                          <input
                            type="text"
                            value={getFieldValue(field, 'display_Label') || ''}
                            onChange={(e) => handleFieldChange(field.id, 'display_Label', e.target.value)}
                            className="fm-input"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step={20}
                            value={getFieldValue(field, 'default_Width') || ''}
                            onChange={(e) => handleFieldChange(field.id, 'default_Width', parseInt(e.target.value) || null)}
                            className="fm-input fm-input-narrow"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={getFieldValue(field, 'display_Order') ?? ''}
                            onChange={(e) => handleFieldChange(field.id, 'display_Order', parseInt(e.target.value) || null)}
                            className="fm-input fm-input-narrow"
                          />
                        </td>
                        <td>
                          <StuiSelect
                            value={getFieldValue(field, 'uI_Type') || 'text'}
                            onChange={(e) => handleFieldChange(field.id, 'uI_Type', e.target.value)}
                            className="fm-select"
                          >
                            {uiTypeOptions.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </StuiSelect>
                        </td>
                        <td className="fm-checkbox-cell">
                          {(() => {
                            const hasFkConfig = getFieldValue(field, 'fK_Table') ||
                              field.foreignKeyTable ||
                              getFieldValue(field, 'fK_Display_Field');
                            return (
                              <button
                                className={`fm-label-config-btn ${hasFkConfig ? 'active' : 'inactive'}`}
                                onClick={() => {
                                  setFkConfigField(field);
                                  if (getFieldValue(field, 'fK_Admin_DB') && adminTables.length === 0) {
                                    adminDynamicApi.getTables()
                                      .then(res => setAdminTables(res.data || []))
                                      .catch(() => {});
                                  }
                                }}
                                title={hasFkConfig ? 'FK configured' : 'Configure FK'}
                              >
                                <Settings size={16} />
                              </button>
                            );
                          })()}
                        </td>
                        <td>
                          <StuiSelect
                            value={getFieldValue(field, 'lookup_Category') || ''}
                            onChange={(e) => handleFieldChange(field.id, 'lookup_Category', e.target.value || null)}
                            className="fm-input"
                          >
                            <option value="">-</option>
                            {lookupCategories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </StuiSelect>
                        </td>
                        <td className="fm-checkbox-cell">
                          {(() => {
                            const hasLabelConfig = getFieldValue(field, 'label_Source_Table') ||
                              getFieldValue(field, 'label_Source_Column') ||
                              getFieldValue(field, 'label_Source_FK');
                            return (
                              <button
                                className={`fm-label-config-btn ${hasLabelConfig ? 'active' : 'inactive'}`}
                                onClick={() => setLabelConfigField(field)}
                                title={hasLabelConfig ? 'Label source configured' : 'Configure label source'}
                              >
                                <Tags size={16} />
                              </button>
                            );
                          })()}
                        </td>
                        <td className="fm-checkbox-cell">
                          <input
                            type="checkbox"
                            checked={getFieldValue(field, 'header_Vertical') || false}
                            onChange={(e) => handleFieldChange(field.id, 'header_Vertical', e.target.checked)}
                            className="stui-checkbox-input"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="fm-no-selection">
              <Database size={48} />
              <p>Select a table to view and edit field metadata</p>
            </div>
          )}
        </div>
      </div>

      {/* FK Config Modal */}
      {fkConfigField && (
        <div className="stui-modal-overlay" onClick={() => setFkConfigField(null)}>
          <div className="stui-modal" onClick={e => e.stopPropagation()}>
            <div className="stui-modal-header">
              <h3>FK Configuration</h3>
              <button type="button" className="stui-modal-close" onClick={() => setFkConfigField(null)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <p className="stui-mb-md" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                Field: <strong>{fkConfigField.field_Name}</strong>
              </p>

              <div className="form-group">
                <label>
                  <span className="stui-checkbox">
                    <input
                      type="checkbox"
                      className="stui-checkbox-input"
                      checked={!!getFieldValue(fkConfigField, 'fK_Admin_DB')}
                      onChange={async (e) => {
                        handleFieldChange(fkConfigField.id, 'fK_Admin_DB', e.target.checked || null);
                        if (e.target.checked && adminTables.length === 0) {
                          try {
                            const res = await adminDynamicApi.getTables();
                            setAdminTables(res.data || []);
                          } catch { setAdminTables([]); }
                        }
                      }}
                    />
                    Admin Database
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label>Table</label>
                <StuiSelect
                  className="form-input"
                  value={getFieldValue(fkConfigField, 'fK_Table') || ''}
                  onChange={async (e) => {
                    handleFieldChange(fkConfigField.id, 'fK_Table', e.target.value || null);
                    const isAdmin = !!getFieldValue(fkConfigField, 'fK_Admin_DB');
                    if (isAdmin && e.target.value && !adminColumnCache[e.target.value]) {
                      try {
                        const res = await adminDynamicApi.getColumns(e.target.value);
                        setAdminColumnCache(prev => ({ ...prev, [e.target.value]: res.data || [] }));
                      } catch { setAdminColumnCache(prev => ({ ...prev, [e.target.value]: [] })); }
                    }
                  }}
                >
                  <option value="">- None -</option>
                  {(getFieldValue(fkConfigField, 'fK_Admin_DB') ? adminTables : tables).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </StuiSelect>
              </div>

              <div className="form-group">
                <label>Display Field</label>
                <StuiSelect
                  className="form-input"
                  value={getFieldValue(fkConfigField, 'fK_Display_Field') || ''}
                  onChange={(e) => handleFieldChange(fkConfigField.id, 'fK_Display_Field', e.target.value || null)}
                  disabled={!getFieldValue(fkConfigField, 'fK_Table')}
                >
                  <option value="">{getFieldValue(fkConfigField, 'fK_Table') ? '- Select Field -' : '- Select table first -'}</option>
                  {(() => {
                    const tbl = getFieldValue(fkConfigField, 'fK_Table');
                    const isAdmin = !!getFieldValue(fkConfigField, 'fK_Admin_DB');
                    const cols = isAdmin ? (adminColumnCache[tbl] || []) : getTableColumns(tbl);
                    return cols.filter(c => c !== 'ID').map(col => (
                      <option key={col} value={col}>{col}</option>
                    ));
                  })()}
                </StuiSelect>
              </div>

              <div className="form-group">
                <label>
                  <span className="stui-checkbox">
                    <input
                      type="checkbox"
                      className="stui-checkbox-input"
                      checked={getFieldValue(fkConfigField, 'fK_Clickable') ?? true}
                      onChange={(e) => handleFieldChange(fkConfigField.id, 'fK_Clickable', e.target.checked)}
                    />
                    Clickable
                  </span>
                </label>
              </div>
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => setFkConfigField(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Label Config Modal */}
      {labelConfigField && (
        <div className="stui-modal-overlay" onClick={() => setLabelConfigField(null)}>
          <div className="stui-modal" onClick={e => e.stopPropagation()}>
            <div className="stui-modal-header">
              <h3>Label Source Config</h3>
              <button type="button" className="stui-modal-close" onClick={() => setLabelConfigField(null)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <p className="stui-mb-md" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                Field: <strong>{labelConfigField.field_Name}</strong>
              </p>

              <div className="form-group">
                <label>Source Table</label>
                <StuiSelect
                  className="form-input"
                  value={getFieldValue(labelConfigField, 'label_Source_Table') || ''}
                  onChange={(e) => handleFieldChange(labelConfigField.id, 'label_Source_Table', e.target.value || null)}
                >
                  <option value="">- None -</option>
                  {tables.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </StuiSelect>
              </div>

              <div className="form-group">
                <label>Source Column</label>
                <StuiSelect
                  className="form-input"
                  value={getFieldValue(labelConfigField, 'label_Source_Column') || ''}
                  onChange={(e) => handleFieldChange(labelConfigField.id, 'label_Source_Column', e.target.value || null)}
                  disabled={!getFieldValue(labelConfigField, 'label_Source_Table')}
                >
                  <option value="">{getFieldValue(labelConfigField, 'label_Source_Table') ? '- Select Column -' : '- Select table first -'}</option>
                  {getTableColumns(getFieldValue(labelConfigField, 'label_Source_Table')).map(col => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </StuiSelect>
              </div>

              <div className="form-group">
                <label>FK Field (join column)</label>
                <StuiSelect
                  className="form-input"
                  value={getFieldValue(labelConfigField, 'label_Source_FK') || ''}
                  onChange={(e) => handleFieldChange(labelConfigField.id, 'label_Source_FK', e.target.value || null)}
                >
                  <option value="">- None -</option>
                  {mergedFields.filter(f => f.foreignKeyTable).map(f => (
                    <option key={f.field_Name} value={f.field_Name}>{f.field_Name}</option>
                  ))}
                </StuiSelect>
              </div>
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => setLabelConfigField(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldManagerPage;
